import { Footer } from "@common/SimpleDrawer/components/Footer";
import { Section } from "@common/SimpleDrawer/components/Section";
import { FormInput } from "@components/FormInput/FormInput";
import { FORM_ACTIONS } from "@models/FormActions";
import { useReducer } from "react";
import { useAuth } from "@Auth/useAuth";
import { useProductStagesStore } from "@stores/productStagesStore";
import { onSuccessShow } from "@utils/onSuccessShow";
import { onErrorShow } from "@utils/onErrorShow";
import { Box } from "@mui/material";

type StageInitialState = {
  name: string;
  description: string;
  errors: Record<keyof StageInitialState, string>;
};

const initialState: StageInitialState = {
  name: "",
  description: "",
  errors: {} as Record<keyof StageInitialState, string>,
};

interface Action<T> {
  type: FORM_ACTIONS;
  field?: keyof T;
  value?: string | number;
  errors?: Record<keyof T, string>;
}

function formReducer(
  state: StageInitialState,
  action: Action<StageInitialState>
) {
  switch (action.type) {
    case FORM_ACTIONS.SET_FIELD:
      return { ...state, [action.field!]: action.value };
    case FORM_ACTIONS.SET_ERRORS:
      return { ...state, errors: action.errors };
    case FORM_ACTIONS.RESET:
      return initialState;
    default:
      return state;
  }
}

type StageFormProps = {
  handleClose: () => void;
};

export const StageForm = ({ handleClose }: StageFormProps) => {
  const { baseUser } = useAuth();
  const { createStage, loading } = useProductStagesStore();
  const [state, dispatch] = useReducer(
    formReducer as React.Reducer<
      Partial<StageInitialState>,
      Action<StageInitialState>
    >,
    initialState
  );

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    dispatch({
      type: FORM_ACTIONS.SET_FIELD,
      field: e.target.name as keyof StageInitialState,
      value: e.target.value,
    });
  };

  const handleFormVerification = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    e.preventDefault();
    const errors = {} as Record<keyof StageInitialState, string>;

    const requiredFields: (keyof StageInitialState)[] = ["name"];

    requiredFields.forEach((field) => {
      if (!state[field]) errors[field] = `${field} is required`;
    });

    if (Object.keys(errors).length > 0) {
      dispatch({ type: FORM_ACTIONS.SET_ERRORS, errors });
      return;
    }
    return true;
  };

  const handleSubmit = async (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    e.preventDefault();
    const validForm = handleFormVerification(e);

    if (!validForm) return;

    const { name, description } = state;

    try {
      await createStage(
        {
          name: name!,
          description,
          organization_id: baseUser!.organization_id!,
        },
        baseUser!.organization_id!
      );
      onSuccessShow("Stage created successfully");
      handleClose();
    } catch (error) {
      console.error(error);
      onErrorShow("Failed to create stage");
    }
  };

  return (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <Box sx={{ flex: 1, overflow: "auto", pb: 2 }}>
        <Section>
          <FormInput
            name="name"
            label="Name"
            value={state.name}
            onChange={handleChange}
            errorMessage={state.errors?.name}
          />
          <FormInput
            name="description"
            label="Description"
            value={state.description}
            onChange={handleChange}
            errorMessage={state.errors?.description}
          />
        </Section>
      </Box>

      <Footer
        loading={loading}
        handleClose={handleClose}
        handleSubmit={handleSubmit}
      />
    </Box>
  );
};
